<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import dayjs from 'dayjs';
import { $api } from '@/services';
import { useStore } from 'vuex';
import LoadingDowloadPDF from '@/views/plansDetail/features/reportTarget/loadingDowloadPDF/loadingDowloadPDF.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import { _ROUTER_NAME } from '@/enums';
import {
	readPackageLock,
	writePackageLock,
} from '@/views/packages/packageLockStorage';
import exclamationIcon from '@/assets/images/icons/warning-fill.png';
import infoIcon from '@/assets/images/packages/info.svg';
import arrowIcon from '@/assets/images/packages/arrow-right.svg';
import arrowBlueIcon from '@/assets/images/packages/arrow-right-blue.svg';

const modalIsOpen = ref(false);
const store = useStore();
const router = useRouter();
const route = useRoute();
const loading = ref(false);
const steps = [
	{ id: 1, label: 'Chọn gói' },
	{ id: 2, label: 'Xác nhận' },
	{ id: 3, label: 'Thanh toán' },
];
const currentStep = ref(1);
const activeContract = ref({});
const studentCount = ref(1);
const defaultSeats = ref(0);
const lockedStudents = ref([]);
const lockNoticeOpen = ref(false);
const startDate = ref(new Date(2027, 0, 1));
const selectedPackageId = ref(null);
const dayLeft = ref(0);
const packages = ref([]);
const centerId = computed(() => store.state.profile.center_id);
const isAccounting = () => {
	return store.state.profile.staff.role === 'accountant';
};
const type = ref(route.query.type || '');
const pricePerStudent = ref(0);

const getPackages = async () => {
	try {
		loading.value = true;
		const res =
			type.value === 'student'
				? await $api.leader.listPackagesByStudent()
				: await $api.leader.listPackages();
		if (type.value === 'student') {
			dayLeft.value = res.data.data.days_left;
			studentCount.value = 1;
			pricePerStudent.value = res.data.data.price?.amount_in_cents;
		} else {
			packages.value = res.data.data.packages || [];
			defaultSeats.value = Number(res.data.data.default_seats) || 0;
			studentCount.value = defaultSeats.value || 1;
			startDate.value = res.data.data.default_start_date;
			const draft = readPackageLock();
			if (draft?.studentCount) {
				studentCount.value = Number(draft.studentCount) || studentCount.value;
			}
			if (draft?.selectedPackageId) {
				selectedPackageId.value = draft.selectedPackageId;
			}
			const gap = Math.max(
				0,
				defaultSeats.value - packageTotalSeats.value
			);
			if (
				gap > 0 &&
				Array.isArray(draft?.students) &&
				draft.students.length === gap
			) {
				lockedStudents.value = draft.students;
			}
		}
		// packages.value.push({
		// 	id: 'kindergarten',
		// 	type: 'Chương trình mẫu giáo hoà nhập',
		// 	duration: 1,
		// 	fee_per_seat: 28000,
		// 	bonus_seats_percent: 0,
		// 	badges: [],
		// });

		loading.value = false;
	} catch (error) {
		loading.value = false;
	}
};

const getVouchers = async () => {
	try {
		loading.value = true;
		const res = await $api.leader.listVouchers();
		vouchers.value = res.data.data;
		loading.value = false;
	} catch (error) {
		loading.value = false;
	}
};

const fetchContactDetail = async () => {
	try {
		const {
			data: { active_contract },
		} = await $api.leader.fetchContact({}, !isAccounting());
		activeContract.value = active_contract || {};
	} catch (e) {
		console.log(e);
	}
};

const formatCurrency = (value) => {
	if (value == null || Number.isNaN(value)) return '-';
	return `₫ ${Number(value).toLocaleString('vi-VN')}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('vi-VN');

const TIER_META = {
	lite: {
		theme: 'lite',
		name: 'Luca Lite',
		badge: 'Phù hợp với nhu cầu cơ bản',
		description:
			'Trợ thủ chuyên môn đắc lực: Hệ thống hóa thư viện mục tiêu, tăng tốc lập IEP và tự động hóa báo cáo buổi học, tuần, tháng',
	},
	standard: {
		theme: 'pro',
		name: 'Luca Pro',
		badge: 'Đầy đủ các tính năng',
		description:
			'Giải pháp quản lý và vận hành toàn diện cho trung tâm trên một nền tảng',
	},
	pro: {
		theme: 'pro',
		name: 'Luca Pro',
		badge: 'Đầy đủ các tính năng',
		description:
			'Giải pháp quản lý và vận hành toàn diện cho trung tâm trên một nền tảng',
	},
};

const TIER_ORDER = ['lite', 'standard', 'pro'];

const formatDate = (value) => {
	if (!value) return '-';
	return dayjs(value).format('DD/MM/YYYY');
};
const formatDate2 = (value) => {
	if (!value) return '-';
	return dayjs(value).format('YYYY-MM-DD');
};
const getGiftedStudents = (count, bonusRate) => {
	if (!bonusRate) return 0;
	// Tặng X% studentCount: >0 và <0.5 làm tròn xuống, >0.5 làm tròn lên
	return Math.round((Number(count) || 0) * bonusRate);
};

// Mua thêm HS: không tặng, tính phí theo đúng số lượng đăng ký
const studentEstimatedTotal = computed(
	() =>
		(Number(studentCount.value) || 0) * (Number(pricePerStudent.value) || 0)
);

const getLargeCenterDiscountPercent = (studentTotal) => {
	if (studentTotal >= 501) return 20;
	if (studentTotal >= 201) return 15;
	if (studentTotal >= 100) return 10;
	return 0;
};

const getPackageStats = (pkg) => {
	const billable = Number(studentCount.value) || 0;
	const bonus = getGiftedStudents(billable, pkg.bonus_seats_percent / 100);
	const total = billable + bonus;
	const perStudentTotal = pkg.fee_per_seat * pkg.duration;
	const estimatedTotal = billable * perStudentTotal;
	const endDate = dayjs(pkg.estimated_end_date);

	return {
		total,
		billable,
		bonus,
		perStudentTotal,
		estimatedTotal,
		endDate,
	};
};

const selectedPackage = computed(() =>
	packages.value.find((pkg) => pkg.id === selectedPackageId.value)
);

const packageGroups = computed(() => {
	const grouped = {};
	const order = [];
	packages.value.forEach((pkg) => {
		const tier = String(pkg.tier || '').toLowerCase();
		if (!grouped[tier]) {
			grouped[tier] = [];
			order.push(tier);
		}
		grouped[tier].push(pkg);
	});
	order.sort((a, b) => {
		const ia = TIER_ORDER.indexOf(a);
		const ib = TIER_ORDER.indexOf(b);
		if (ia === -1 && ib === -1) return 0;
		if (ia === -1) return 1;
		if (ib === -1) return -1;
		return ia - ib;
	});
	return order.map((tier) => {
		const items = grouped[tier]
			.slice()
			.sort((a, b) => Number(a.duration) - Number(b.duration));
		const meta = TIER_META[tier] || {
			theme: 'default',
			name: tier || 'Gói dịch vụ',
			badge: '',
			description: '',
		};
		const prices = items
			.map((pkg) => Number(pkg.fee_per_seat) || 0)
			.filter((price) => price > 0);
		return {
			tier,
			...meta,
			packages: items,
			displayPrice: prices.length ? Math.min(...prices) : 0,
		};
	});
});

const summary = computed(() => {
	if (!selectedPackage.value) {
		return {
			name: '-',
			package: '-',
			package_tier: '',
			costLabel: '-',
			startDate: formatDate(startDate.value),
			endDate: '-',
			billable: '-',
			bonus: '-',
			total: '-',
			estimatedTotal: null,
			largeCenterDiscountPercent: 0,
			largeCenterDiscount: 0,
			kidTimeDiscount: 0,
		};
	}
	const stats = getPackageStats(selectedPackage.value);
	const largeCenterDiscountPercent = getLargeCenterDiscountPercent(
		stats.billable
	);
	let kidTimeDiscount = 0;
	let kidTimeDiscountPercent = 20;
	if (centerId.value === 1) {
		kidTimeDiscount = Math.round(
			(stats.estimatedTotal * kidTimeDiscountPercent) / 100
		);
	}
	const largeCenterDiscount =
		largeCenterDiscountPercent > 0
			? Math.round(
					(stats.estimatedTotal * largeCenterDiscountPercent) / 100
			  )
			: 0;
	const tierKey = String(selectedPackage.value.tier || '').toLowerCase();
	const tierMeta = TIER_META[tierKey];
	const packageLabel = selectedPackage.value.duration
		? `${selectedPackage.value.duration} tháng`
		: selectedPackage.value.type;
	return {
		name: (() => {
			if (selectedPackage.value.id === 'kindergarten') {
				return 'Chương trình mẫu giáo hoà nhập';
			}
			return tierMeta?.name
				? `${tierMeta.name} - ${packageLabel}`
				: 'Gói ' + selectedPackage.value.type;
		})(),
		package:
			selectedPackage.value.id === 'kindergarten'
				? 'Chương trình mẫu giáo hoà nhập'
				: packageLabel,
		package_tier: tierMeta?.theme === 'lite' ? 'lite' : 'pro',
		costLabel: `${formatCurrency(
			selectedPackage.value.fee_per_seat * selectedPackage.value.duration
		)}/${selectedPackage.value.duration} tháng/ học sinh`,
		startDate: formatDate(startDate.value),
		endDate: formatDate(stats.endDate),
		billable: stats.billable,
		bonus: stats.bonus > 0 ? stats.bonus : '-',
		total: stats.total,
		estimatedTotal: stats.estimatedTotal,
		largeCenterDiscountPercent,
		largeCenterDiscount,
		kidTimeDiscountPercent,
		kidTimeDiscount,
	};
});

const packageTotalSeats = computed(() => {
	if (selectedPackage.value) {
		return getPackageStats(selectedPackage.value).total;
	}
	return Number(studentCount.value) || 0;
});

const seatsToLock = computed(() => {
	if (type.value === 'student') return 0;
	return Math.max(0, defaultSeats.value - packageTotalSeats.value);
});

const lockReady = computed(
	() =>
		seatsToLock.value > 0 &&
		lockedStudents.value.length === seatsToLock.value
);

const needsStudentLock = computed(
	() =>
		type.value !== 'student' &&
		seatsToLock.value > 0 &&
		!lockReady.value
);

const canContinue = computed(() => {
	if (type.value === 'student') return true;
	if (!selectedPackage.value) return false;
	return true;
});

// Step 2
const promoCode = ref('');
const promoError = ref('');
const appliedPromo = ref(null);
const selectedVoucherId = ref(null);
const needInvoice = ref(false);
const invoiceForm = ref({
	tax_id: '',
	company_name: '',
	address: '',
	email: '',
	contact_phone: '',
});

const vouchers = ref([]);

const selectedVouchers = computed(() =>
	vouchers.value.filter((v) => v.id === selectedVoucherId.value)
);

const voucherBaseTotal = computed(() => {
	if (type.value === 'student') return studentEstimatedTotal.value || 0;
	return summary.value.estimatedTotal || 0;
});

const getVoucherDiscount = (voucher) => {
	if (!voucher) return 0;
	if (voucher.discount_type === 'percent') {
		const percent = Number(voucher.discount_percent) || 0;
		return Math.round((voucherBaseTotal.value * percent) / 100);
	}
	return Number(voucher.discount) || 0;
};

const voucherDiscountTotal = computed(() =>
	selectedVouchers.value.reduce(
		(sum, voucher) => sum + getVoucherDiscount(voucher),
		0
	)
);

const promoDiscount = computed(() => {
	if (!appliedPromo.value || !summary.value.estimatedTotal) return 0;
	if (appliedPromo.value.discount_percent) {
		return Math.round(
			(summary.value.estimatedTotal *
				appliedPromo.value.discount_percent) /
				100
		);
	}
	return appliedPromo.value.amount || 0;
});

const finalTotal = computed(() => {
	if (summary.value.estimatedTotal == null) return null;
	return Math.max(
		0,
		summary.value.estimatedTotal -
			summary.value.kidTimeDiscount -
			summary.value.largeCenterDiscount -
			voucherDiscountTotal.value -
			promoDiscount.value
	);
});

const applyPromo = async () => {
	try {
		const res = await $api.leader.checkDiscountCode(
			promoCode.value.trim(),
			selectedPackage.value.id
		);
		if (res.data.data) {
			if (res.data.data.message) {
				appliedPromo.value = null;
				promoError.value = res.data.data.message;

				setTimeout(() => {
					promoError.value = '';
				}, 3000);
			} else appliedPromo.value = res.data.data;
		}
	} catch (error) {
		console.error(error);
	}
};

const toggleVoucher = (id) => {
	selectedVoucherId.value = selectedVoucherId.value === id ? null : id;
};

const isVoucherSelected = (id) => selectedVoucherId.value === id;

// Step 3
const paymentSubmitted = ref(false);
const paymentProof = ref(null);
const paymentProofPreview = ref('');
const paymentProofInputId = 'buying-package-payment-proof';
const centerName = computed(() => store.state.profile.current_org.center_name);

const canSubmitPayment = computed(() => !!paymentProof.value);

const copyText = async (text) => {
	try {
		await navigator.clipboard.writeText(text);
		await store.dispatch('setAlertMessage', {
			message: 'Đã sao chép thành công!',
			type: 'success',
		});
	} catch (e) {
		console.error(e);
	}
};

const openPaymentProofPicker = () => {
	const input = document.getElementById(paymentProofInputId);
	if (input) input.click();
};

const onPaymentProofChange = (event) => {
	const file = event?.target?.files?.[0];
	if (!file) return;
	const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
	if (!allowed.includes(file.type)) {
		event.target.value = null;
		return;
	}
	if (paymentProofPreview.value) {
		URL.revokeObjectURL(paymentProofPreview.value);
	}
	paymentProof.value = file;
	paymentProofPreview.value = file.type.startsWith('image/')
		? URL.createObjectURL(file)
		: '';
	event.target.value = null;
};

const removePaymentProof = () => {
	if (paymentProofPreview.value) {
		URL.revokeObjectURL(paymentProofPreview.value);
	}
	paymentProof.value = null;
	paymentProofPreview.value = '';
};

const key = ref('');

const getSignatures = async () => {
	try {
		const fileList = [paymentProof.value];
		const {
			data: { data },
		} = await $api.file.getSignatures({
			mime_types: fileList.map((f) => f.type),
			prefix: 'contract_payment_screenshot',
		});
		if (data && data.length) {
			for (let i = 0; i < data.length; i++) {
				let formData = new FormData();
				formData.append(
					'AWSAccessKeyId',
					data[0]['AWSAccessKeyId'] || ''
				);
				formData.append('key', data[i].key || '');
				formData.append('acl', data[i].acl || '');
				formData.append('Content-Type', data[i]['Content-Type'] || '');
				formData.append('x-amz-signature', data[i].signature || '');
				formData.append('Signature', data[i].signature || '');
				formData.append('Policy', data[i].policy || '');
				formData.append(
					'success_action_status',
					data[i].success_action_status || ''
				);
				formData.append('file', fileList[i]);
				await uploadToS3(data[i].action, formData);
			}
		}
	} catch (error) {
		console.error(error);
	}
};

const uploadToS3 = async (url, payload) => {
	try {
		const { data } = await $api.file.uploadToS3(url, payload);
		const keyData = data.toString().match('<Key>(.*)</Key>');
		const assetKey = keyData && keyData.length ? keyData[1] : '';
		key.value = assetKey;
	} catch (e) {
		console.error(e);
	}
};

const contract = ref(null);
const submitPayment = async () => {
	if (!canSubmitPayment.value) return;
	await getSignatures();
	if (key.value) {
		try {
			const payload = {
				payment_screenshot_asset_key: key.value,
			};
			if (type.value !== 'student') {
				payload.start_date = formatDate2(startDate.value);
				payload.package_id = selectedPackage.value.id;
				payload.seats = summary.value.billable;
			} else {
				payload.seats = studentCount.value;
			}
			if (needInvoice.value) {
				payload.invoice = invoiceForm.value;
			} else {
				payload.invoice = {};
			}
			if (selectedVoucherId.value)
				payload.voucher_ids = [selectedVoucherId.value];
			if (promoCode.value) payload.referral_code = promoCode.value;
			const res =
				type.value === 'student'
					? await $api.leader.buySeats(payload)
					: await $api.leader.createContractPayment(payload);
			if (res?.data) {
				contract.value = res.data.data;
				paymentSubmitted.value = true;
				const createdId =
					res.data.data?.id ||
					res.data.data?.contract_id ||
					res.data.data?.contract?.id;
				if (type.value !== 'student' && lockReady.value) {
					if (!createdId) {
						store.dispatch('setAlertMessage', {
							message:
								'Đã tạo gói nhưng chưa lưu được danh sách học sinh cần khoá.',
							type: 'danger',
						});
					} else {
						const draft = readPackageLock() || {};
						writePackageLock({
							...draft,
							contractId: createdId,
						});
						try {
							await $api.leader.closeContractStudents(
								createdId,
								lockedStudents.value.map((student) => student.id)
							);
						} catch (closeError) {
							const errors = closeError?.response?.data?.errors;
							store.dispatch('setAlertMessage', {
								message:
									Array.isArray(errors) && errors.length
										? errors[0]
										: 'Không thể lưu danh sách học sinh cần khoá.',
								type: 'danger',
							});
						}
					}
				}
			}
		} catch (e) {
			if (e?.response?.data?.errors.length > 0) {
				store.dispatch('setAlertMessage', {
					message: e?.response?.data?.errors[0],
					type: 'danger',
				});
			}
		}
	}
};

const decreaseStudents = () => {
	if (studentCount.value > 1) studentCount.value -= 1;
};

const increaseStudents = () => {
	studentCount.value += 1;
};

const onStudentInput = (event) => {
	const value = Number(event.target.value.replace(/\D/g, ''));
	studentCount.value = value > 0 ? value : 1;
};

const openLockStudents = () => {
	const draft = readPackageLock() || {};
	writePackageLock({
		...draft,
		studentCount: Number(studentCount.value) || 0,
		totalSeats: packageTotalSeats.value,
		defaultSeats: defaultSeats.value,
		selectedPackageId: selectedPackageId.value,
		students: lockReady.value ? lockedStudents.value : [],
	});
	lockNoticeOpen.value = false;
	router.push({
		name: _ROUTER_NAME.PACKAGE_LOCK_STUDENTS,
		query: {
			required: String(seatsToLock.value),
			...(draft.contractId
				? { contractId: String(draft.contractId) }
				: {}),
		},
	});
};

watch([studentCount, selectedPackageId], () => {
	if (type.value === 'student' || !defaultSeats.value) return;
	if (lockedStudents.value.length && !lockReady.value) {
		lockedStudents.value = [];
	}
	const draft = readPackageLock() || {};
	writePackageLock({
		...draft,
		studentCount: Number(studentCount.value) || 0,
		totalSeats: packageTotalSeats.value,
		defaultSeats: defaultSeats.value,
		selectedPackageId: selectedPackageId.value,
		students: lockReady.value ? lockedStudents.value : [],
	});
});

const goBack = () => {
	router.push('/packages');
};

const goToStep = (stepId) => {
	if (paymentSubmitted.value) return;
	// Chỉ cho back về step trước; không cho nhảy sang step sau qua stepper
	if (stepId < currentStep.value) {
		currentStep.value = stepId;
	}
};

const goNext = () => {
	if (currentStep.value === 1 && !canContinue.value) return;
	if (currentStep.value === 1 && needsStudentLock.value) {
		lockNoticeOpen.value = true;
		return;
	}
	currentStep.value = Math.min(currentStep.value + 1, steps.length);
};

const downloadInvoice = async () => {
	const contractId = contract.value?.invoice?.id;
	if (!contractId) return;

	const url =
		process.env.VUE_APP_PDF_URL +
		'packages/invoice/' +
		contractId +
		'?token=' +
		localStorage.getItem('tk') +
		'&subdomain=' +
		localStorage.getItem('sub-domain');
	const params = {
		url: url,
		format: 'a2',
		id_render: 'package_invoice_pdf',
	};
	try {
		modalIsOpen.value = true;
		const response = await fetch(
			'https://g4ridpplxi.execute-api.ap-southeast-1.amazonaws.com/',
			{
				method: 'POST',
				body: JSON.stringify(params),
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		);
		if (!response.ok) {
			modalIsOpen.value = false;
			window.open(url + '&is_print=true', '_blank');
			return;
		}
		const data = await response.json();
		if (data.data) {
			modalIsOpen.value = false;
			const linkSource = `data:application/pdf;base64, ${data.data}`;
			const link = document.createElement('a');
			const fileName = 'Hoadon.pdf';
			link.href = linkSource;
			link.download = fileName;
			link.click();
		}
	} catch (e) {
		window.open(url + '&is_print=true', '_blank');
		console.log(e);
	}
};

onMounted(() => {
	fetchContactDetail();
	getPackages();
});

const getBillingInfo = async () => {
	try {
		const res = await $api.leader.getBillingInfo();
		if (res.data.data !== null) invoiceForm.value = res.data.data;
	} catch (error) {
		console.log(error);
	}
};

const contractPayment = ref({
	qr_code_url: '',
	account_holder_name: '',
	account_number: '',
	bank_name: '',
	branch: '',
	swift_code: '',
	transfer_details: '',
});
const getContractPayment = async () => {
	try {
		loading.value = true;
		const res = await $api.leader.getContractPayment(
			undefined,
			needInvoice.value ? 1 : 2
		);
		contractPayment.value = res.data.data;
		loading.value = false;
	} catch (error) {
		loading.value = false;
	}
};

watch(currentStep, (newVal) => {
	if (newVal === 2) {
		getVouchers();
		getBillingInfo();
	}
	if (newVal === 3) {
		getContractPayment();
	}
});
</script>


<template>
	<DaModalFrame v-model:open="modalIsOpen">
		<LoadingDowloadPDF :text="'Đang tải xuống file PDF hoá đơn...'" />
	</DaModalFrame>
	<div
		v-if="lockNoticeOpen"
		class="lock-notice"
		@click.self="lockNoticeOpen = false"
	>
		<div class="lock-notice__card">
			<div class="lock-notice__body">
				<img
					src="@/assets/images/illustrations/warning.png"
					alt=""
					width="60"
					height="60"
				/>
				<h2>Số học sinh đăng ký ít hơn hiện tại</h2>
				<p>
					Số học sinh đăng ký mới ít hơn số học sinh hiện có của trung tâm.
					Vui lòng chọn {{ seatsToLock }} học sinh để khóa khi gói mới được
					kích hoạt.
				</p>
				<p>
					Các học sinh được chọn vẫn tiếp tục hoạt động bình thường với gói
					hiện tại và chỉ bị khóa khi gói mới bắt đầu.
				</p>
			</div>
			<div class="lock-notice__footer">
				<button type="button" @click="lockNoticeOpen = false">Đóng</button>
				<button type="button" class="button-p" @click="openLockStudents">
					Chọn học sinh để khóa
				</button>
			</div>
		</div>
	</div>
	<div class="buying-package px-12 md-down:px-4 pb-10 pt-10 md-down:pt-6">
		<div
			class="buying-package__header relative flex items-center gap-2 mb-8 md-down:mb-6 md-down:justify-center"
		>
			<button
				type="button"
				class="buying-package__back flex-shrink-0 cursor-pointer md-down:absolute md-down:left-0 md-down:top-1/2"
				@click="goBack"
			>
				<img
					class="w-4 h-4"
					src="@/assets/images/icons/arrow-right.png"
					alt="back"
				/>
			</button>
			<h1
				class="text-4xl font-bold text-neutral-700 md-down:text-xl md-down:text-center md-down:px-8"
			>
				Đăng ký
				{{
					type === 'student' ? 'thêm học sinh' : 'gói Luca Education'
				}}
			</h1>
		</div>
		<div
			v-if="currentStep >= 1 && currentStep <= 3"
			class="grid grid-cols-12 md-down:grid-cols-1 gap-6 md-down:gap-4 items-start"
		>
			<div
				class="col-span-8 md-down:col-span-full md-down:order-2 w-full bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden"
			>
				<div class="stepper mb-4 md-down:mb-0">
					<div
						class="flex w-full md:min-w-[540px] md-down:flex-col md-down:min-w-0"
					>
						<div
							v-for="(step, index) in steps"
							:key="step.id"
							class="step-item flex items-center gap-3 px-6 py-3.5 relative flex-1 md-down:flex-none md-down:w-full md-down:px-4 md-down:py-3.5 md-down:gap-3"
							:class="{
								'step-item--active':
									!paymentSubmitted &&
									currentStep === step.id,
								'step-item--done':
									paymentSubmitted || currentStep > step.id,
								'step-item--clickable':
									!paymentSubmitted && step.id < currentStep,
							}"
							@click="goToStep(step.id)"
						>
							<div
								class="step-number w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
							>
								<svg
									v-if="
										paymentSubmitted ||
										currentStep > step.id
									"
									width="14"
									height="10"
									viewBox="0 0 14 10"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M1 5L5 9L13 1"
										stroke="white"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
								<template v-else>
									{{ String(step.id).padStart(2, '0') }}
								</template>
							</div>
							<div
								class="step-label text-sm font-semibold whitespace-nowrap"
							>
								{{ step.label }}
							</div>

							<svg
								v-if="index < steps.length - 1"
								class="step-divider md-down:hidden"
								fill="none"
								height="72"
								viewBox="0 0 12 72"
								width="12"
								xmlns="http://www.w3.org/2000/svg"
								aria-hidden="true"
							>
								<mask
									:id="'step-chevron-mask-' + step.id"
									fill="black"
									height="72"
									maskUnits="userSpaceOnUse"
									width="13"
									x="-1"
									y="0"
								>
									<rect
										fill="white"
										height="72"
										width="13"
										x="-1"
									/>
									<path d="M0 0V27.2195L10 36L0 44.7805V72" />
								</mask>
								<path
									d="M0 27.2195H-1V27.6722L-0.659801 27.971L0 27.2195ZM10 36L10.6598 36.7514L11.5156 36L10.6598 35.2486L10 36ZM0 44.7805L-0.659801 44.029L-1 44.3278V44.7805H0ZM-1 0V27.2195H1V0H-1ZM-0.659801 27.971L9.3402 36.7514L10.6598 35.2486L0.659801 26.4681L-0.659801 27.971ZM9.3402 35.2486L-0.659801 44.029L0.659801 45.5319L10.6598 36.7514L9.3402 35.2486ZM-1 44.7805V72H1V44.7805H-1Z"
									fill="#CBD5E1"
									:mask="
										'url(#step-chevron-mask-' +
										step.id +
										')'
									"
								/>
							</svg>
						</div>
					</div>
				</div>
				<div v-if="currentStep === 1">
					<div class="p-6 md-down:p-4 border-b border-neutral-100">
						<div
							class="flex items-center justify-between mb-6 md-down:mb-4 gap-2"
						>
							<div
								class="text-lg font-semibold text-neutral-600 md-down:text-base"
							>
								Đăng ký
								{{
									type === 'student'
										? 'thêm học sinh'
										: 'gói dịch vụ'
								}}
							</div>
							<a
								v-if="type !== 'student'"
								href="https://www.luca.education/chi-phi"
								target="_blank"
								rel="noopener"
								class="text-sm text-green-600 underline"
							>
								Tìm hiểu thêm
							</a>
						</div>

						<div
							class="grid grid-cols-1 md:grid-cols-2 gap-6 md-down:gap-4 mb-8 md-down:mb-6"
						>
							<div>
								<label
									class="block text-neutral-700 mb-2 md-down:text-sm"
								>
									Số lượng học sinh tính phí
								</label>
								<div
									class="student-stepper flex items-center border rounded-lg overflow-hidden bg-white focus-within:ring-1"
									:class="
										seatsToLock > 0
											? 'border-sienna-500 focus-within:border-sienna-500 focus-within:ring-sienna-500'
											: 'border-neutral-200 focus-within:border-yellow-500 focus-within:ring-yellow-500'
									"
								>
									<button
										type="button"
										class="w-12 h-12 text-xl text-neutral-500 hover:bg-neutral-50"
										@click="decreaseStudents"
									>
										−
									</button>
									<input
										class="flex-1 h-12 text-center border-0 text-neutral-700 font-semibold focus:outline-none focus:ring-0"
										:value="studentCount"
										inputmode="numeric"
										@input="onStudentInput"
									/>
									<button
										type="button"
										class="w-12 h-12 text-xl text-neutral-500 hover:bg-neutral-50"
										@click="increaseStudents"
									>
										+
									</button>
								</div>
							</div>

							<div v-if="type !== 'student'">
								<label
									class="block text-neutral-700 mb-2 md-down:text-sm"
								>
									Ngày bắt đầu gói mới
								</label>
								<VueDatepicker
									v-model="startDate"
									autoApply
									class="input-date w-full"
									format="dd/MM/yyyy"
									placeholder="Chọn ngày bắt đầu"
									uid="buying-package-start-date"
								>
									<template #input-icon>
										<img
											class="w-5 h-5"
											src="@/assets/images/icons/calendar.png"
											alt=""
										/>
									</template>
								</VueDatepicker>
							</div>
						</div>

						<div
							v-if="type !== 'student' && seatsToLock > 0 && !lockReady"
							class="seat-alert seat-alert--warn"
						>
							<img :src="exclamationIcon" alt="" width="20" height="20" />
							<div>
								<p>
									Trung tâm hiện có
									<b>{{ defaultSeats }} học sinh</b>, trong khi gói mới
									sử dụng
									<b>{{ packageTotalSeats }} học sinh</b>. Sau khi thanh
									toán, vui lòng khóa
									<b>{{ seatsToLock }} học sinh</b>
									để tiếp tục.
								</p>
								<button type="button" @click="lockNoticeOpen = true">
									Khoá học sinh
									<img :src="arrowIcon" alt="" width="16" height="16" />
								</button>
							</div>
						</div>
						<div
							v-else-if="type !== 'student' && lockReady"
							class="seat-alert seat-alert--info"
						>
							<img :src="infoIcon" alt="" width="20" height="20" />
							<div>
								<p>
									Đã chọn
									<b>{{ lockedStudents.length }} học sinh</b>
									để khóa và sẽ được hệ thống tự động khóa khi gói mới bắt
									đầu.
								</p>
								<button type="button" @click="openLockStudents">
									Xem & chỉnh sửa
									<img :src="arrowBlueIcon" alt="" width="16" height="16" />
								</button>
							</div>
						</div>

						<div
							v-if="type === 'student'"
							class="text-neutral-700 mb-2"
						>
							Gói dịch vụ (số học sinh đăng ký thêm này sẽ được
							thêm vào gói hiện tại)
						</div>
						<div class="text-neutral-700 mb-2" v-else>
							Chọn gói dịch vụ
						</div>
						<div class="space-y-3" v-if="type === 'student'">
							<label
								class="package-card package-card--student flex cursor-pointer rounded-xl border overflow-hidden transition border-yellow-500 shadow-sm"
							>
								<div
									class="flex items-start gap-4 md-down:gap-3 p-4 md-down:p-4 bg-yellow-50 w-full"
								>
									<input
										class="mt-1.5 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 h-4 w-4 flex-shrink-0"
										type="radio"
										name="buying-package"
										:value="true"
										checked
									/>

									<div class="flex-1 min-w-0">
										<div
											class="flex items-start justify-between gap-3 mb-2"
										>
											<div
												class="font-semibold text-neutral-700 text-lg md-down:text-base"
											>
												Gói
												{{ activeContract.package }}
											</div>
											<div
												v-if="
													Number(
														activeContract.months
													) === 6
												"
												class="bg-neutral-700 text-white text-center px-3 py-1 rounded-full text-sm md-down:text-xs font-semibold flex-shrink-0 whitespace-nowrap"
											>
												Tiết kiệm nhất
											</div>
										</div>
										<div
											class="flex items-end justify-between gap-6 md-down:flex-col md-down:items-start md-down:gap-2"
										>
											<div
												class="text-3xl md-down:text-2xl font-semibold text-neutral-700 whitespace-nowrap"
											>
												{{ formatCurrency(68000)
												}}<span
													class="text-sm font-medium text-neutral-400"
													>/tháng</span
												>
											</div>
											<div
												class="package-card__note text-xs text-neutral-500 text-right md-down:text-left leading-snug md-down:max-w-none"
											>
												Chi phí thêm học sinh chỉ được
												tính theo ngày, tính từ thời
												điểm này đến hết gói hiện tại
											</div>
										</div>
									</div>
								</div>
							</label>
						</div>
						<div class="tier-groups" v-else>
							<div
								v-for="group in packageGroups"
								:key="group.tier || 'default'"
								class="tier-group"
								:class="
									'tier-group--' + (group.theme || 'default')
								"
							>
								<div class="tier-group__head">
									<div class="tier-group__title-row">
										<div class="tier-group__identity">
											<div class="tier-group__name">
												<img
													v-if="
														group.theme === 'lite'
													"
													src="@/assets/images/icons/package-leaf.svg"
													alt=""
												/>
												<img
													v-else-if="
														group.theme === 'pro'
													"
													src="@/assets/images/icons/package-crown.svg"
													alt=""
												/>
												<span>{{ group.name }}</span>
											</div>
											<span
												v-if="group.badge"
												class="tier-group__badge"
												>{{ group.badge }}</span
											>
										</div>
										<div
											v-if="group.displayPrice"
											class="tier-group__price"
										>
											<span class="tier-group__amount">{{
												formatNumber(group.displayPrice)
											}}</span>
											<span class="tier-group__unit"
												>/học sinh<br />/tháng</span
											>
										</div>
									</div>
									<p
										v-if="group.description"
										class="tier-group__desc"
									>
										{{ group.description }}
									</p>
								</div>
								<div class="tier-group__options">
									<label
										v-for="pkg in group.packages"
										:key="pkg.id"
										class="tier-option"
										:class="{
											'tier-option--selected':
												selectedPackageId === pkg.id,
										}"
										@click="selectedPackageId = pkg.id"
									>
										<div class="tier-option__top">
											<span
												class="tier-option__radio-wrap"
											>
												<input
													v-model="selectedPackageId"
													class="tier-option__radio"
													type="radio"
													name="buying-package"
													:value="pkg.id"
												/>
												<span
													class="tier-option__radio-face"
												></span>
											</span>
											<div class="tier-option__body">
												<div
													class="tier-option__title-row"
												>
													<div
														class="tier-option__title"
													>
														<span
															>Gói
															{{ pkg.duration }}
															tháng</span
														>
														<span
															v-if="
																pkg.bonus_seats_percent >
																0
															"
															class="tier-option__gift"
															:class="
																Number(
																	pkg.duration
																) >= 6
																	? 'tier-option__gift--gold'
																	: 'tier-option__gift--blue'
															"
														>
															<img
																v-if="
																	Number(
																		pkg.duration
																	) >= 6
																"
																src="@/assets/images/icons/package-gift-gold.svg"
																alt=""
															/>
															<img
																v-else
																src="@/assets/images/icons/package-gift-blue.svg"
																alt=""
															/>
															Tặng
															{{
																pkg.bonus_seats_percent
															}}% tổng số HS
														</span>
													</div>
													<span
														v-if="
															group.theme ===
																'pro' &&
															Number(
																pkg.duration
															) === 6
														"
														class="tier-option__best"
														>Tiết kiệm nhất</span
													>
												</div>
												<div
													class="tier-option__prices"
												>
													<div
														class="tier-option__month"
													>
														<span
															class="tier-option__dong"
															>đ</span
														>
														<span
															class="tier-option__fee"
															>{{
																formatNumber(
																	pkg.fee_per_seat
																)
															}}</span
														>
														<span
															class="tier-option__per"
															>/tháng</span
														>
													</div>
													<div
														class="tier-option__total"
													>
														<span
															class="tier-option__dong tier-option__dong--sm"
															>đ</span
														>
														<span
															>{{
																formatNumber(
																	getPackageStats(
																		pkg
																	)
																		.perStudentTotal
																)
															}}/{{
																pkg.duration
															}}
															tháng/học sinh</span
														>
													</div>
												</div>
											</div>
										</div>
										<div class="tier-option__foot">
											<div>
												Tổng sử dụng:
												<span class="font-semibold"
													>{{
														getPackageStats(pkg)
															.total
													}}
													học sinh</span
												>
											</div>
											<div class="tier-option__fee-col">
												<div>
													{{
														pkg.bonus_seats_percent >
															0 &&
														getPackageStats(pkg)
															.bonus > 0
															? 'Chỉ tính phí:'
															: 'Tính phí:'
													}}
													<span class="font-semibold"
														>{{
															getPackageStats(pkg)
																.billable
														}}
														học sinh</span
													>
												</div>
												<div
													v-if="
														pkg.bonus_seats_percent >
															0 &&
														getPackageStats(pkg)
															.bonus > 0
													"
													class="tier-option__bonus"
												>
													<span>Tặng thêm:</span>
													<img
														v-if="
															Number(
																pkg.duration
															) >= 6
														"
														src="@/assets/images/icons/package-gift-gold-sm.svg"
														alt=""
													/>
													<img
														v-else
														src="@/assets/images/icons/package-gift-blue-sm.svg"
														alt=""
													/>
													<span
														:class="
															Number(
																pkg.duration
															) >= 6
																? 'tier-option__bonus--gold'
																: 'tier-option__bonus--blue'
														"
														>{{
															getPackageStats(pkg)
																.bonus
														}}
														học sinh</span
													>
												</div>
											</div>
										</div>
									</label>
								</div>
							</div>
						</div>
					</div>

					<div class="p-6 md-down:p-4 flex justify-end">
						<button
							type="button"
							class="continue-btn w-1/2 md-down:w-full h-12 rounded-lg font-semibold transition"
							:class="
								canContinue
									? 'button-p'
									: 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
							"
							:disabled="!canContinue"
							@click="goNext"
						>
							Tiếp tục
						</button>
					</div>
				</div>
				<div v-else-if="currentStep === 2">
					<div class="p-6 md-down:p-4">
						<!-- Promo code -->
						<div class="mb-8 md-down:mb-6">
							<label class="block text-neutral-700 mb-2">
								Mã khuyến mãi
							</label>
							<div class="flex gap-3 md-down:flex-col">
								<input
									v-model="promoCode"
									type="text"
									class="flex-1 h-12 rounded-lg border border-neutral-200 px-4 text-neutral-700 focus:border-yellow-500 focus:ring-yellow-500"
									placeholder="Nhập mã khuyến mãi"
									@keyup.enter="applyPromo"
								/>
								<button
									type="button"
									class="button-p h-12 px-6 w-max whitespace-nowrap md-down:w-full"
									@click="applyPromo"
								>
									Áp dụng
								</button>
							</div>
							<div
								v-if="promoError"
								class="mt-2 flex items-center gap-2 text-xs text-sienna-600"
							>
								<span
									class="w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center flex-shrink-0"
									>!</span
								>
								{{ promoError }}
							</div>
							<div
								v-if="appliedPromo"
								class="mt-2 flex items-center gap-2 text-sm text-green-600"
							>
								<img
									src="@/assets/images/icons/tick.png"
									alt=""
									class="w-4 h-4"
								/>
								Áp dụng mã khuyến mãi thành công
							</div>
							<div
								class="mt-3 flex items-start gap-2 text-sm text-neutral-500"
							>
								<img
									src="@/assets/images/icons/info-circle-line.png"
									alt=""
									class="w-4 h-4 mt-0.5 flex-shrink-0"
								/>
								<div>
									Chương trình 'Lan toả Luca', áp dụng ưu đãi
									20% cho các bên tham gia.
									<a
										href="https://www.luca.education/affiliate"
										target="_blank"
										rel="noopener"
										class="underline text-green-600"
										>Tìm hiểu thêm</a
									>
								</div>
							</div>
						</div>

						<!-- Vouchers -->
						<div
							class="mb-8 md-down:mb-6 flex flex-wrap gap-3"
							v-if="vouchers.length"
						>
							<label
								v-for="voucher in vouchers"
								:key="voucher.id"
								class="voucher-card flex gap-3 p-4 md-down:p-3 rounded-xl border cursor-pointer transition w-max flex-shrink-0"
								:class="
									isVoucherSelected(voucher.id)
										? 'border-yellow-500 bg-yellow-50'
										: 'border-yellow-200 bg-yellow-50/60 hover:border-yellow-400'
								"
								@click.prevent="toggleVoucher(voucher.id)"
							>
								<span
									class="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1"
									:class="
										isVoucherSelected(voucher.id)
											? 'border-yellow-500'
											: 'border-neutral-300'
									"
								>
									<span
										v-if="isVoucherSelected(voucher.id)"
										class="w-2.5 h-2.5 rounded-full bg-yellow-500"
									></span>
								</span>
								<div>
									<div class="text-neutral-700 text-sm">
										{{ voucher.display_voucher_name }}
									</div>
									<div
										class="flex items-center gap-1.5 text-sm text-neutral-700 font-semibold"
									>
										Giảm
										{{
											voucher.discount_type === 'percent'
												? voucher.discount_percent + '%'
												: Number(
														voucher.discount || 0
												  ).toLocaleString('vi-VN') +
												  'đ'
										}}
										<!-- <img
											src="@/assets/images/icons/info-circle-line.png"
											alt=""
											class="w-4 h-4 opacity-60"
										/> -->
									</div>
								</div>
							</label>
						</div>
						<div class="border-t border-neutral-100 pt-6">
							<label
								class="flex items-center gap-3 cursor-pointer select-none mb-4"
							>
								<input
									v-model="needInvoice"
									type="checkbox"
									class="h-5 w-5 rounded border-neutral-300 text-yellow-500 focus:ring-yellow-500"
								/>
								<span class="text-neutral-700"
									>Bạn có nhu cầu xuất hoá đơn</span
								>
							</label>

							<div v-if="needInvoice">
								<div
									class="text-lg font-semibold text-neutral-700 mb-4"
								>
									Thông tin xuất hoá đơn
								</div>
								<div
									class="grid grid-cols-1 md:grid-cols-2 gap-4"
								>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Mã số thuế</label
										>
										<input
											v-model="invoiceForm.tax_id"
											type="text"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập mã số thuế"
										/>
									</div>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Tên công ty</label
										>
										<input
											v-model="invoiceForm.company_name"
											type="text"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập tên công ty"
										/>
									</div>
									<div class="md:col-span-2">
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Địa chỉ</label
										>
										<input
											v-model="invoiceForm.address"
											type="text"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập địa chỉ"
										/>
									</div>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>Email nhận hóa đơn</label
										>
										<input
											v-model="invoiceForm.email"
											type="email"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập email nhận hóa đơn"
										/>
									</div>
									<div>
										<label
											class="block text-sm text-neutral-600 mb-2"
											>SĐT liên hệ</label
										>
										<input
											v-model="invoiceForm.contact_phone"
											type="tel"
											class="w-full h-12 rounded-lg border border-neutral-200 px-4"
											placeholder="Nhập SĐT liên hệ"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div
						class="p-6 md-down:p-4 flex justify-end border-t border-neutral-100"
					>
						<button
							type="button"
							class="button-p h-12 px-8 w-full md:w-1/2 font-semibold"
							@click="goNext"
						>
							Tiến hành thanh toán
						</button>
					</div>
				</div>
				<div v-else-if="currentStep === 3 && !paymentSubmitted">
					<input
						:id="paymentProofInputId"
						type="file"
						hidden
						accept="image/png,image/jpeg,image/jpg,application/pdf"
						@change="onPaymentProofChange"
					/>

					<div class="p-6 md:p-8 md-down:p-4">
						<div
							class="text-lg font-semibold text-neutral-600 mb-6 md-down:mb-4 md-down:text-base"
						>
							Thanh toán chuyển khoản
						</div>

						<div
							class="flex flex-col items-center mb-8 md-down:mb-6"
						>
							<img
								:src="contractPayment?.qr_code_url"
								alt="VietQR"
								class="w-56 h-56 md-down:w-full md-down:max-w-[280px] md-down:h-auto object-contain bg-white"
							/>
						</div>

						<div
							class="max-w-2xl mx-auto space-y-4 text-sm md-down:space-y-3"
						>
							<div
								class="flex justify-between gap-4 items-start md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400 flex-shrink-0"
									>Số tài khoản</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left flex items-center gap-2"
								>
									{{ contractPayment.account_number }}
									<button
										type="button"
										class="flex-shrink-0"
										@click="
											copyText(
												contractPayment.account_number
											)
										"
									>
										<img
											src="@/assets/images/icons/copy.png"
											alt="copy"
											class="w-4 h-4"
										/>
									</button>
								</span>
							</div>
							<div
								class="flex justify-between gap-4 md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400"
									>Tên chủ tài khoản</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left"
									>{{
										contractPayment.account_holder_name
									}}</span
								>
							</div>
							<div
								class="flex justify-between gap-4 md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400"
									>Tên ngân hàng</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left"
									>{{ contractPayment.bank_name }}</span
								>
							</div>
							<div
								class="flex justify-between gap-4 md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400"
									>Chi nhánh</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left"
									>{{ contractPayment.branch }}</span
								>
							</div>
							<div
								class="flex justify-between gap-4 md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400"
									>Swiftcode</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left"
									>{{ contractPayment.swift_code }}</span
								>
							</div>
							<div
								class="flex justify-between gap-4 items-start md-down:flex-col md-down:gap-1"
							>
								<span
									class="text-neutral-700 md-down:text-neutral-400 flex-shrink-0"
									>Nội dung chuyển khoản</span
								>
								<span
									class="font-semibold text-neutral-700 text-right md-down:text-left flex items-start gap-2 break-words"
								>
									<span class="min-w-0">{{
										contractPayment.transfer_details
									}}</span>
									<button
										type="button"
										class="flex-shrink-0 mt-0.5"
										@click="
											copyText(
												contractPayment.transfer_details
											)
										"
									>
										<img
											src="@/assets/images/icons/copy.png"
											alt="copy"
											class="w-4 h-4"
										/>
									</button>
								</span>
							</div>
						</div>
					</div>

					<div
						class="border-t border-neutral-100 p-6 md:p-8 md-down:p-4"
					>
						<div
							class="text-lg font-semibold text-neutral-700 mb-4 md-down:text-base"
						>
							Chứng từ thanh toán
						</div>

						<div
							v-if="!paymentProof"
							class="upload-zone w-full rounded-xl border border-dashed border-neutral-200 bg-neutral-50 py-10 px-6 md-down:py-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-yellow-500 transition"
							@click="openPaymentProofPicker"
						>
							<img
								src="@/assets/images/icons/cloud-upload-fill.png"
								alt=""
								class="w-8 h-8 mb-3"
							/>
							<div class="font-semibold text-neutral-700 mb-1">
								Tải lên chứng từ thanh toán
							</div>
							<div class="text-sm text-neutral-400">
								PNG, JPG & PDF
							</div>
						</div>
						<div v-else class="relative inline-block md-down:block">
							<div
								class="absolute top-2 right-2 cursor-pointer z-10"
								@click="removePaymentProof"
							>
								<svg
									width="24"
									height="24"
									viewBox="0 0 12 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<g opacity="0.5">
										<rect
											width="12"
											height="12"
											rx="6"
											fill="black"
										/>
									</g>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
										fill="white"
									/>
								</svg>
							</div>
							<img
								v-if="paymentProofPreview"
								:src="paymentProofPreview"
								alt="Chứng từ thanh toán"
								class="max-w-xs max-h-64 md-down:max-w-full rounded-lg object-contain border border-neutral-200"
							/>
							<div
								v-else
								class="px-6 py-8 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-600 text-sm"
							>
								{{ paymentProof.name }}
							</div>
						</div>
					</div>

					<div
						class="border-t border-neutral-100 p-6 md-down:p-4 flex justify-end"
					>
						<button
							type="button"
							class="h-12 px-10 rounded-lg font-semibold transition w-1/2 md-down:w-full"
							:class="
								canSubmitPayment
									? 'button-p'
									: 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
							"
							:disabled="!canSubmitPayment"
							@click="submitPayment"
						>
							Nộp
						</button>
					</div>
				</div>
				<div v-else>
					<div class="p-6 md:p-8 md-down:p-4">
						<div
							class="success-banner rounded-xl px-5 py-4 flex items-start gap-3 mb-8 md-down:mb-6"
						>
							<img
								src="@/assets/images/icons/tick.png"
								alt=""
								class="w-5 h-5 mt-0.5 flex-shrink-0"
							/>
							<div>
								<div class="font-semibold text-green-800 mb-1">
									Đăng ký thành công
								</div>
								<!-- <div class="text-sm text-green-700">
									Chúng tôi đã nhận được yêu cầu đăng ký gói
									dịch vụ của bạn. Luca sẽ xác nhận thanh toán
									trong vòng 48 giờ.
								</div> -->
							</div>
						</div>
						<div
							class="text-lg font-semibold text-neutral-600 mb-4"
						>
							Chi tiết hoá đơn
						</div>
						<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
							<div>
								<div class="text-neutral-700 mb-1">
									Mã hóa đơn
								</div>
								<div class="font-semibold text-neutral-700">
									{{
										contract?.invoice?.invoice_number ||
										contract?.invoice_number
									}}
								</div>
							</div>
							<div>
								<div class="text-sm text-neutral-400 mb-1">
									Ngày tạo
								</div>
								<div class="font-semibold text-neutral-700">
									{{ dayjs(Date.now()).format('DD/MM/YYYY') }}
								</div>
							</div>
						</div>
						<div>
							<div class="text-neutral-700 mb-1">Trạng thái</div>
							<div class="text-neutral-700">
								<span class="font-semibold"
									>Chờ xác nhận thanh toán</span
								>
								<span>
									(Bạn vẫn có thể sử dụng Luca Education trong
									thời gian chờ xác nhận)</span
								>
							</div>
						</div>

						<div class="mb-8" v-if="needInvoice">
							<div class="border-t border-neutral-100 pt-8 mb-8">
								<div
									class="text-lg font-semibold text-neutral-700 mb-4"
								>
									Thông tin xuất hoá đơn
								</div>
								<div
									class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
								>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											MST
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{ invoiceForm.tax_id }}
										</div>
									</div>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											Công ty
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{ invoiceForm.company_name }}
										</div>
									</div>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											SĐT
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{ invoiceForm.contact_phone }}
										</div>
									</div>
									<div>
										<div
											class="text-sm text-neutral-400 mb-1"
										>
											Email
										</div>
										<div
											class="font-semibold text-neutral-700"
										>
											{{ invoiceForm.email }}
										</div>
									</div>
								</div>
								<div>
									<div class="text-sm text-neutral-400 mb-1">
										Địa chỉ
									</div>
									<div class="font-semibold text-neutral-700">
										{{ invoiceForm.address }}
									</div>
								</div>
							</div>
						</div>

						<div class="border-t border-neutral-100 pt-8">
							<div
								class="text-lg font-semibold text-neutral-700 mb-4"
							>
								Chứng từ thanh toán
							</div>
							<img
								v-if="paymentProofPreview"
								:src="paymentProofPreview"
								alt="Chứng từ thanh toán"
								class="max-w-xs max-h-64 rounded-lg object-contain border border-neutral-200 mb-3"
							/>
							<div
								v-else-if="paymentProof"
								class="mb-3 text-sm text-neutral-600"
							>
								{{ paymentProof.name }}
							</div>
						</div>
					</div>

					<div
						class="border-t border-neutral-100 p-6 md-down:p-4 flex justify-end"
					>
						<button
							type="button"
							class="button-p h-12 px-8 font-semibold w-max md-down:w-full"
							@click="goBack"
						>
							Trở về trang chính
						</button>
					</div>
				</div>
			</div>
			<div
				class="buying-package__summary col-span-4 md-down:col-span-full md-down:order-1 md:sticky md:top-6 md:z-10 w-full bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden text-sm"
			>
				<!-- Header -->
				<div
					v-if="paymentSubmitted"
					class="px-5 py-3 flex justify-between items-center gap-2 border-b border-neutral-100"
				>
					<span class="font-semibold text-neutral-700">Hoá đơn</span>
					<span class="text-neutral-400"
						>Số:
						{{
							contract?.invoice?.invoice_number ||
							contract?.invoice_number
						}}</span
					>
				</div>
				<div
					v-if="activeContract.package || paymentSubmitted"
					class="current-package px-5 py-4"
					:class="{ 'space-y-1': paymentSubmitted }"
				>
					<template v-if="paymentSubmitted">
						<div class="font-semibold text-neutral-700">
							{{ centerName }}
						</div>
						<div class="text-neutral-600">
							Người tạo:
							{{ contract?.created_by_staff?.full_name }}
						</div>
						<div class="text-neutral-600">
							Ngày tạo:
							{{ dayjs(Date.now()).format('DD/MM/YYYY') }}
						</div>
					</template>
					<template v-else-if="activeContract.package">
						<div class="space-y-2 text-neutral-700">
							<div class="flex justify-between gap-2">
								<span class="font-semibold text-neutral-600"
									>Gói hiện tại</span
								>
								<div
									class="font-semibold flex items-center gap-1"
								>
									<img
										v-if="
											activeContract.package_tier ===
											'lite'
										"
										src="@/assets/images/icons/package-leaf.svg"
										alt=""
										class="w-5 h-5"
									/>
									<img
										v-if="
											activeContract.package_tier ===
											'pro'
										"
										src="@/assets/images/icons/package-crown.svg"
										alt=""
										class="w-5 h-5"
									/>
									<span
										:class="
											activeContract.package_tier ===
											'lite'
												? 'text-lime-600'
												: activeContract.package_tier ===
												  'pro'
												? 'text-amber'
												: 'text-neutral-600'
										"
										>{{
											activeContract.package_tier ===
											'lite'
												? 'Luca Lite -'
												: activeContract.package_tier ===
												  'pro'
												? 'Luca Pro - '
												: ''
										}}
										{{ activeContract.package }}
									</span>
								</div>
							</div>
							<div class="flex justify-between gap-2">
								<span>Số học sinh</span>
								<span>{{ activeContract.seats + activeContract.bonus_seats || 0 }}</span>
							</div>
							<div class="flex justify-between gap-2">
								<span>Hết hạn</span>
								<span
									>23:59
									{{
										$filters.formatDate2(
											activeContract.end_date
										)
									}}</span
								>
							</div>
						</div>
					</template>
				</div>

				<!-- Package + students -->
				<div
					class="px-5 py-4 space-y-2 border-b border-neutral-100"
					v-if="type === 'student'"
				>
					<div class="flex justify-between gap-2 text-neutral-700">
						<span>Đăng ký thêm</span>
						<span>{{ studentCount }}</span>
					</div>
					<div class="flex justify-between gap-2 text-neutral-700">
						<span>Chi phí</span>
						<span>{{ formatCurrency(68000) }}/tháng/học sinh</span>
					</div>
					<div class="flex justify-between gap-2 text-neutral-700">
						<span>Thời hạn sử dụng</span>
						<span>{{ dayLeft }} ngày</span>
					</div>
				</div>
				<div
					class="px-5 py-4 space-y-2 border-b border-neutral-100"
					v-else
				>
					<div class="flex justify-between gap-2">
						<span class="font-semibold text-neutral-600"
							>Gói đăng ký mới</span
						>
						<div class="font-semibold flex items-center gap-1">
							<img
								v-if="summary.package_tier === 'lite'"
								src="@/assets/images/icons/package-leaf.svg"
								alt=""
								class="w-5 h-5"
							/>
							<img
								v-if="summary.package_tier === 'pro'"
								src="@/assets/images/icons/package-crown.svg"
								alt=""
								class="w-5 h-5"
							/>
							<span
								:class="
									summary.package_tier === 'lite'
										? 'text-lime-600'
										: summary.package_tier === 'pro'
										? 'text-amber'
										: 'text-neutral-600'
								"
								>{{
									summary.package_tier === 'lite'
										? 'Luca Lite -'
										: summary.package_tier === 'pro'
										? 'Luca Pro - '
										: ''
								}}
								{{ summary.package }}</span
							>
						</div>
					</div>
					<div class="flex justify-between gap-2 text-neutral-700">
						<span>Chi phí</span>
						<span>{{ summary.costLabel }}</span>
					</div>
					<div class="flex justify-between gap-2 text-neutral-700">
						<span>Ngày bắt đầu</span>
						<span>00:00 {{ summary.startDate }}</span>
					</div>
					<div class="flex justify-between gap-2 text-neutral-700">
						<span>Ngày kết thúc</span>
						<span v-if="summary.endDate && summary.endDate !== '-'"
							>23:59 {{ summary.endDate }}</span
						>
						<span v-else>-</span>
					</div>
					<div
						class="border-t border-neutral-100 pt-2 space-y-2 text-neutral-700"
					>
						<div class="flex justify-between gap-2">
							<span>Số học sinh tính phí</span>
							<span>{{ summary.billable }}</span>
						</div>
						<div
							v-if="summary.bonus > 0"
							class="flex justify-between gap-2"
						>
							<span>Tặng thêm</span>
							<span>{{ summary.bonus }}</span>
						</div>
						<div class="flex justify-between gap-2 font-semibold">
							<span>Tổng sử dụng</span>
							<span>{{ summary.total }}</span>
						</div>
					</div>
				</div>

				<!-- Totals -->
				<div class="summary-total px-5 py-4 space-y-2">
					<div
						class="flex justify-between gap-2 font-semibold text-neutral-700"
					>
						<span>Tổng tạm tính</span>
						<span v-if="type === 'student'">{{
							Number(studentEstimatedTotal).toLocaleString(
								'vi-VN'
							)
						}}</span>
						<span v-else>
							{{
								summary.estimatedTotal != null
									? Number(
											summary.estimatedTotal
									  ).toLocaleString('vi-VN')
									: '-'
							}}
						</span>
					</div>
					<div
						v-if="summary.kidTimeDiscount > 0"
						class="flex justify-between gap-2"
					>
						<span class="text-neutral-600">
							Ưu đãi cho KidTime ({{
								summary.kidTimeDiscountPercent
							}}%)
						</span>
						<span class="text-green-600 font-medium">
							-{{
								Number(summary.kidTimeDiscount).toLocaleString(
									'vi-VN'
								)
							}}
						</span>
					</div>
					<div
						v-if="summary.largeCenterDiscountPercent > 0"
						class="flex justify-between gap-2"
					>
						<span class="text-neutral-600">
							Ưu đãi cho trung tâm lớn ({{
								summary.largeCenterDiscountPercent
							}}%)
						</span>
						<span class="text-green-600 font-medium">
							-{{
								Number(
									summary.largeCenterDiscount
								).toLocaleString('vi-VN')
							}}
						</span>
					</div>
					<div
						v-for="voucher in selectedVouchers"
						:key="'discount-' + voucher.id"
						class="flex justify-between gap-2"
					>
						<span class="text-neutral-600">
							{{ voucher.display_voucher_name }}
							<template
								v-if="voucher.discount_type === 'percent'"
							>
								({{ voucher.discount_percent }}%)
							</template>
						</span>
						<span class="text-green-600 font-medium"
							>-{{
								Number(
									getVoucherDiscount(voucher)
								).toLocaleString('vi-VN')
							}}</span
						>
					</div>
					<div
						v-if="promoDiscount > 0"
						class="flex justify-between gap-2"
					>
						<span class="text-neutral-600"
							>Ưu đãi "Lan toả Luca"</span
						>
						<span class="text-green-600 font-medium"
							>-{{
								Number(promoDiscount).toLocaleString('vi-VN')
							}}</span
						>
					</div>
					<div
						class="border-t border-orange-100 pt-2 flex justify-between items-center gap-2"
						v-if="type !== 'student'"
					>
						<span class="font-semibold text-neutral-700"
							>Thành tiền</span
						>
						<span class="font-bold text-neutral-800 text-xl">
							{{
								finalTotal != null
									? Number(finalTotal).toLocaleString('vi-VN')
									: '-'
							}}
						</span>
					</div>
				</div>

				<div
					v-if="paymentSubmitted"
					class="px-6 py-4 flex items-center justify-center"
				>
					<button
						type="button"
						class="flex items-center gap-2 text-blue-600 font-semibold text-xs"
						@click="downloadInvoice"
					>
						<img
							src="@/assets/images/icons/cloud-download-fill.png"
							alt=""
							class="w-4 h-4"
						/>
						Tải xuống hoá đơn
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.buying-package__back img {
	transform: rotate(180deg);
}

.seat-alert {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 24px;
	padding: 16px;
	border-radius: 5px;

	img {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
	}

	p {
		font-size: 14px;
		line-height: 20px;
	}

	b {
		font-weight: 600;
	}

	button {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-top: 8px;
		font-size: 14px;
		line-height: 20px;
		font-weight: 600;
	}
}

.seat-alert--warn {
	background: #fcf1ee;
	border: 1px solid #e37056;
	color: #db5a41;
}

.seat-alert--info {
	background: #eef3f8;
	border: 1px solid #bacde4;
	color: #4477b2;
}

.lock-notice {
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(107, 114, 128, 0.75);
}

.lock-notice__card {
	width: 600px;
	max-width: 100%;
	overflow: hidden;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.1), 0 20px 24px rgba(20, 37, 63, 0.06);
}

.lock-notice__body {
	padding: 24px 48px;
	text-align: center;

	img {
		width: 60px;
		height: 60px;
		margin: 0 auto;
	}

	h2 {
		margin: 20px 0 8px;
		color: #344256;
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}

	p {
		color: #65758b;
		font-size: 16px;
		line-height: 24px;
	}

	p + p {
		margin-top: 16px;
	}
}

.lock-notice__footer {
	display: flex;
	gap: 24px;
	padding: 24px 48px;
	background: #f8fafc;

	button {
		flex: 1;
		padding: 12px 18px;
		color: #65758b;
		font-size: 14px;
		line-height: 20px;
		font-weight: 600;
	}

	.button-p {
		color: #344256;
		border-radius: 4px;
	}
}

@media (min-width: 768px) {
	.buying-package__summary {
		max-height: calc(100vh - 3rem);
		overflow-y: auto;
	}
}

@media (min-width: 768px) {
	.package-card--student .package-card__note {
		max-width: 280px;
	}
}

@media (max-width: 767px) {
	.buying-package__back {
		transform: translateY(-50%);
	}
}

.stepper {
	background: #fff;
	border-bottom: 1px solid #e2e8f0;
	overflow: hidden;
}

.step-item {
	color: #94a3b8;
	background: #fff;
	min-width: 0;
	position: relative;
	cursor: default;
}

.step-item--clickable {
	cursor: pointer;
}

.step-number {
	background: #f1f5f9;
	color: #94a3b8;
	border: 2px solid #cbd5e1;
	box-sizing: border-box;
}

.step-label {
	color: inherit;
}

.step-item--active {
	color: #e5a42b;
}

.step-item--active::after {
	content: '';
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 3px;
	background: #e5a42b;
	z-index: 2;
}

.step-item--active .step-number {
	background: #fff;
	color: #e5a42b;
	border-color: #e5a42b;
}

.step-item--done {
	color: #334155;
}

.step-item--done .step-number {
	background: #e5a42b;
	color: #fff;
	border-color: #e5a42b;
}

.step-divider {
	position: absolute;
	left: 100%;
	top: 50%;
	transform: translateY(-50%);
	height: 100%;
	width: 12px;
	pointer-events: none;
	z-index: 3;
}

@media (max-width: 767px) {
	.stepper {
		border-bottom: none;
	}

	.step-item {
		border-bottom: 1px solid #e2e8f0;
	}

	.step-item--active::after {
		height: 2px;
	}

	.step-number {
		background: #fff;
	}

	.step-label {
		overflow: visible;
		text-overflow: unset;
	}
}

.badge--blue {
	background: #eef3f8;
	color: #6390c3;
}

.badge--orange {
	background: #f9e3b7;
	color: #ab7b1f;
}

.badge--dark {
	background: #475569;
	color: #fff;
}

.current-package {
	background: #f1f5f9;
	border-bottom: 1px solid #e1e7ef;
}

.summary-total {
	background: #fff7ed;
}

.success-banner {
	background: #e8f5ee;
}

:deep(.dp__input) {
	height: 48px;
	border-radius: 0.5rem;
}

@media (max-width: 767px) {
	.summary-total .text-xl {
		font-size: 1.125rem;
	}
}

.tier-groups {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.tier-group {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px;
	border-radius: 8px;
	border: 1px solid #e1e7ef;
	background: #f8fafc;
}

.tier-group--lite {
	background: #f7fee7;
	border-color: #bef264;
}

.tier-group--pro {
	background: #fffbf3;
	border-color: #f6d692;
}

.tier-group__title-row {
	display: flex;
	align-items: flex-start;
	gap: 12px;
}

.tier-group__identity {
	display: flex;
	flex: 1;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
	min-width: 0;
}

.tier-group__name {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 20px;
	line-height: 28px;
	font-weight: 600;
	color: #344256;
}

.tier-group__name img,
.tier-option__gift img,
.tier-option__bonus img {
	flex-shrink: 0;
}

.tier-group__badge {
	padding: 4px 12px;
	border-radius: 200px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
}

.tier-group--lite .tier-group__badge {
	background: #e0f4bb;
	color: #65a30d;
}

.tier-group--pro .tier-group__badge {
	background: #f9e3b7;
	color: #d89210;
}

.tier-group__price {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-shrink: 0;
	padding-left: 40px;
}

.tier-group__amount {
	font-size: 33px;
	line-height: 40px;
	font-weight: 600;
	letter-spacing: -0.66px;
	color: #344256;
}

.tier-group--lite .tier-group__amount {
	color: #16a34a;
}

.tier-group--pro .tier-group__amount {
	color: #e5a42b;
}

.tier-group__unit {
	font-size: 12px;
	line-height: 16px;
	color: #344256;
}

.tier-group__desc {
	margin-top: 12px;
	font-size: 16px;
	line-height: 24px;
	color: #344256;
}

.tier-group__options {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.tier-option {
	display: block;
	background: #f1f5f9;
	border: 1px solid #cbd5e1;
	border-radius: 12px;
	cursor: pointer;
	overflow: hidden;
}

.tier-group--lite .tier-option--selected {
	border-color: #84cc16;
	background: #f7fee7;
}

.tier-group--pro .tier-option--selected {
	border-color: #f0bb4f;
	background: #fef8ed;
}

.tier-option__top {
	display: flex;
	align-items: flex-start;
	gap: 16px;
	padding: 16px;
	background: #fff;
	border-bottom: 1px solid #cbd5e1;
}

.tier-option__radio-wrap {
	position: relative;
	width: 20px;
	height: 20px;
	margin-top: 2px;
	flex-shrink: 0;
}

.tier-option__radio {
	position: absolute;
	inset: 0;
	z-index: 1;
	margin: 0;
	opacity: 0;
	cursor: pointer;
}

.tier-option__radio-face {
	display: block;
	width: 20px;
	height: 20px;
	box-sizing: border-box;
	border: 2px solid #cbd5e1;
	border-radius: 50%;
	background: #fff;
}

.tier-group--lite .tier-option--selected .tier-option__radio-face {
	border-color: #16a34a;
	background: radial-gradient(circle, #16a34a 0 5px, #fff 6px);
}

.tier-group--pro .tier-option--selected .tier-option__radio-face {
	border-color: #e5a42b;
	background: radial-gradient(circle, #e5a42b 0 5px, #fff 6px);
}

.tier-option__body {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.tier-option__title-row {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 8px;
}

.tier-option__title {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	font-size: 18px;
	line-height: 24px;
	font-weight: 600;
	color: #344256;
}

.tier-option__gift {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 4px 12px;
	border-radius: 40px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
}

.tier-option__gift--blue {
	background: #eef3f8;
	color: #6390c3;
}

.tier-option__gift--gold {
	background: #f9e3b7;
	color: #ab7b1f;
}

.tier-option__best {
	background: #344256;
	color: #fff;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	padding: 4px 12px 2px;
	border-radius: 84px;
	white-space: nowrap;
	flex-shrink: 0;
}

.tier-option__prices {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 8px;
}

.tier-option__month {
	display: flex;
	align-items: center;
	gap: 4px;
	color: #344256;
}

.tier-option__dong {
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
}

.tier-option__dong--sm {
	font-size: 12px;
	line-height: 16px;
	font-weight: 400;
}

.tier-option__fee {
	font-size: 23px;
	line-height: 28px;
	font-weight: 600;
	letter-spacing: -0.46px;
}

.tier-option__per,
.tier-option__total {
	font-size: 16px;
	line-height: 24px;
	color: #344256;
}

.tier-option__total {
	display: flex;
	align-items: flex-start;
	gap: 4px;
	text-align: right;
}

.tier-option__foot {
	display: flex;
	justify-content: space-between;
	gap: 12px;
	padding: 16px;
	font-size: 16px;
	line-height: 24px;
	color: #344256;
}

.tier-option__fee-col {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	text-align: right;
}

.tier-option__bonus {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 4px;
	color: #344256;
}

.tier-option__bonus--blue {
	color: #6390c3;
	font-weight: 600;
}

.tier-option__bonus--gold {
	color: #d89210;
	font-weight: 600;
}

@media (max-width: 767px) {
	.tier-group {
		padding: 16px;
	}

	.tier-group__title-row,
	.tier-option__prices,
	.tier-option__foot {
		flex-direction: column;
		align-items: flex-start;
	}

	.tier-group__price {
		padding-left: 0;
	}

	.tier-group__amount {
		font-size: 28px;
		line-height: 34px;
	}

	.tier-option__fee-col,
	.tier-option__bonus {
		align-items: flex-start;
		text-align: left;
		justify-content: flex-start;
	}
}
</style>
